import api from '../utils/api';
import { db, ALL_TABLES } from './db';
import { drainableOps, remapTempIdReferences } from './opQueue';
import { reportFromError } from './connectivity';

export interface DrainResult {
  pushed: number;
  succeeded: number;
  conflicts: number;
  errors: number;
}

/**
 * Push all pending offline ops to the server via POST /sync/push and reconcile
 * the results into the replica + queue:
 *  - success: for a create, replace the temp optimistic row with the server
 *    record (temp-id -> server-id); then remove the op.
 *  - conflict: keep the op, mark it 'conflict' (server version is newer) — goes
 *    to the visible Needs-review list; never silently overwritten.
 *  - error: keep the op, mark it 'error' + bump its retry count. It is retried
 *    on the next drain until MAX_OP_RETRIES, then parks terminally — and it is
 *    surfaced to the user (errorCount) the whole time, never silently dropped.
 * A push network/server failure leaves every op 'pending'/'error' for the next
 * drain (nothing is lost before a server ack).
 */
export async function drainQueue(): Promise<DrainResult> {
  const result: DrainResult = { pushed: 0, succeeded: 0, conflicts: 0, errors: 0 };
  const ops = await drainableOps();
  if (!ops.length) return result;
  result.pushed = ops.length;

  const operations = ops.map((op) => ({
    id: op.id,
    entityType: op.entityType,
    operationType: op.operationType,
    entityId: op.entityId,
    payload: op.payload,
    clientTimestamp: op.clientTimestamp,
    // Server-origin base version for clock-skew-immune conflict detection (B2);
    // undefined for creates and legacy ops, which the server handles as before.
    baseUpdatedAt: op.baseUpdatedAt,
  }));

  let results: any[];
  try {
    const { data } = await api.post('/sync/push', { operations });
    results = (data && data.results) || [];
  } catch (e) {
    // Network/server failure mid-drain — leave ops pending, retry next time.
    // Record a transport failure so a pull-ok/push-dropped sync doesn't leave
    // the app claiming "online" with stranded ops (the 30s probe then doubles
    // as the drain-retry timer).
    reportFromError(e);
    return result;
  }

  const byId = new Map(ops.map((o) => [o.id, o]));
  for (const r of results) {
    const op = byId.get(r.operationId);
    if (!op) continue;
    const tbl = (db as any)[op.table];

    if (r.status === 'success') {
      result.succeeded += 1;
      if (op.operationType === 'create' && r.serverId) {
        // Wide tx: re-pointing temp->server id touches sibling/child ops in
        // arbitrary tables + their optimistic rows (see remapTempIdReferences).
        await db.transaction('rw', ALL_TABLES.map((t) => (db as any)[t]), async () => {
          // Remap BEFORE removing the temp row, so any child op/row that
          // referenced this temp id is re-pointed to the real server id even if
          // that child errored in this same batch (otherwise it keeps a dead FK
          // and parks terminally — a silently stranded clinical record).
          await remapTempIdReferences(op.entityId, r.serverId);
          await tbl.delete(op.entityId); // remove the temp optimistic row
          // Install the real server row — UNLESS it's absent. A FRESH create always echoes its
          // body (the server suppresses `serverRecord` only for a caller who lacks `view:` on that
          // entity, and it makes an exception for the caller's own create precisely so this branch
          // does not drop the record). So an absent body here means an idempotent REPLAY: either
          // the entity was deleted server-side since, the caller can no longer view that entity
          // type, or someone has edited it since (INV-151; the next pull brings that row). Dropping
          // the temp row is right for all three (not a ghost, a row we may not read, or stale).
          if (r.serverRecord) await tbl.put(r.serverRecord);
          await db.opQueue.delete(op.id);
        });
      } else {
        await db.transaction('rw', tbl, db.opQueue, async () => {
          // An UPDATE body can be absent too: the server withholds `serverRecord` from a caller
          // with `manage:` but not `view:`, and from a replay of a row edited since (INV-151). Keeping
          // the local optimistic row is right then; the next pull brings the canonical row.
          if (op.operationType === 'update' && r.serverRecord) {
            await tbl.put(r.serverRecord); // canonical server row
          } else if (op.operationType === 'delete') {
            await tbl.delete(op.entityId); // ensure gone (covers restore-then-retry)
          }
          await db.opQueue.delete(op.id);
        });
      }
    } else if (r.status === 'conflict') {
      result.conflicts += 1;
      await db.transaction('rw', tbl, db.opQueue, async () => {
        if (op.operationType === 'update' && r.serverRecord) {
          await tbl.put(r.serverRecord); // server wins the display (no silent LWW)
        } else if (op.operationType === 'delete' && op.serverRecord) {
          await tbl.put(op.serverRecord); // restore the optimistically-removed row
        }
        await db.opQueue.update(op.id, {
          status: 'conflict',
          message: r.message,
          serverRecord: r.serverRecord ?? op.serverRecord,
        });
      });
    } else {
      result.errors += 1;
      await db.transaction('rw', tbl, db.opQueue, async () => {
        if (op.operationType === 'delete' && op.serverRecord) {
          await tbl.put(op.serverRecord); // restore on a failed delete
        }
        await db.opQueue.update(op.id, {
          status: 'error',
          message: r.message,
          retries: (op.retries || 0) + 1,
        });
      });
    }
  }

  return result;
}
